<script>
/**
 * Presentational filter bar (Lovable FilterBar → Vue Options API).
 * Parent owns filter state; this emits patches via update:filters.
 * Search commits only after ≥4 characters (or when cleared).
 */
const SEARCH_MIN_CHARS = 4;
const SEARCH_DEBOUNCE_MS = 350;

export default {
    name: 'FilterBar',
    props: {
        filters: {
            type: Object,
            default: () => ({
                query: '',
                channel: 'all',
                category: 'all',
                priority: 'all',
                assignee: 'all',
                statuses: ['new', 'in_progress', 'closed_group'],
            }),
        },
        channelOptions: { type: Array, default: () => [] },
        categoryOptions: { type: Array, default: () => [] },
        priorityOptions: { type: Array, default: () => [] },
        assigneeOptions: { type: Array, default: () => [] },
        statusOptions: {
            type: Array,
            default: () => [
                { value: 'new', label: 'Nuovo' },
                { value: 'in_progress', label: 'In lavorazione' },
                { value: 'closed_group', label: 'Chiusi' },
            ],
        },
        showClassicToggle: { type: Boolean, default: true },
        classicDisabled: { type: Boolean, default: false },
        showSimulate: { type: Boolean, default: false },
    },
    emits: ['update:filters', 'classic-view', 'simulate'],
    data() {
        return {
            statusMenuOpen: false,
            localQuery: this.filters?.query || '',
            queryDebounceTimer: null,
        };
    },
    computed: {
        selectFilters() {
            return [
                {
                    key: 'channel',
                    label: 'Canale',
                    allLabel: 'Tutti i canali',
                    options: this.channelOptions,
                },
                {
                    key: 'category',
                    label: 'Categoria',
                    allLabel: 'Tutte le categorie',
                    options: this.categoryOptions,
                },
                {
                    key: 'priority',
                    label: 'Priorità',
                    allLabel: 'Tutte le priorità',
                    options: this.priorityOptions,
                },
                {
                    key: 'assignee',
                    label: 'Assegnatario',
                    allLabel: 'Tutti gli assegnatari',
                    options: this.assigneeOptions,
                },
            ];
        },
        statusCount() {
            return Array.isArray(this.filters.statuses) ? this.filters.statuses.length : 0;
        },
    },
    watch: {
        'filters.query'(value) {
            const next = value || '';
            if (next !== this.localQuery) {
                this.localQuery = next;
            }
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.onDocumentClick);
        if (this.queryDebounceTimer) {
            clearTimeout(this.queryDebounceTimer);
        }
    },
    methods: {
        optionValue(option) {
            if (option && typeof option === 'object') {
                return option.value ?? option.id ?? option.name;
            }
            return option;
        },
        optionLabel(option) {
            if (option && typeof option === 'object') {
                return option.label ?? option.name ?? String(option.value ?? option.id);
            }
            return String(option);
        },
        patch(partial) {
            this.$emit('update:filters', { ...this.filters, ...partial });
        },
        onQuery(event) {
            this.localQuery = event.target.value;
            if (this.queryDebounceTimer) {
                clearTimeout(this.queryDebounceTimer);
            }
            this.queryDebounceTimer = setTimeout(() => {
                this.commitQuery();
            }, SEARCH_DEBOUNCE_MS);
        },
        commitQuery() {
            const trimmed = (this.localQuery || '').trim();
            let next = '';
            if (trimmed.length === 0) {
                next = '';
            } else if (trimmed.length >= SEARCH_MIN_CHARS) {
                next = trimmed;
            } else {
                // 1–3 chars: do not search yet; clear any previous needle.
                next = '';
            }
            if (next !== (this.filters.query || '')) {
                this.patch({ query: next });
            }
        },
        onSelectKey(key, event) {
            this.patch({ [key]: event.target.value });
        },
        isStatusChecked(status) {
            return Array.isArray(this.filters.statuses) && this.filters.statuses.includes(status);
        },
        toggleStatus(status) {
            const current = Array.isArray(this.filters.statuses) ? [...this.filters.statuses] : [];
            const next = current.includes(status)
                ? current.filter((item) => item !== status)
                : [...current, status];
            this.patch({ statuses: next });
        },
        toggleStatusMenu() {
            this.statusMenuOpen = !this.statusMenuOpen;
        },
        onDocumentClick(event) {
            if (!this.$el.contains(event.target)) {
                this.statusMenuOpen = false;
            }
        },
    },
};
</script>

<template>
    <div class="tickets-index-filters">
        <div class="tickets-index-filters__row">
            <div class="tickets-index-filters__search">
                <span class="tickets-index-filters__search-icon" aria-hidden="true">⌕</span>
                <input
                    type="search"
                    :value="localQuery"
                    placeholder="Cerca id, titolo o email (min. 4 caratteri)..."
                    aria-label="Cerca ticket"
                    @input="onQuery"
                />
            </div>

            <select
                v-for="filter in selectFilters"
                :key="filter.key"
                class="tickets-index-filters__select"
                :value="filters[filter.key]"
                :aria-label="'Filtra per ' + filter.label.toLowerCase()"
                @change="onSelectKey(filter.key, $event)"
            >
                <option value="all">{{ filter.allLabel }}</option>
                <option
                    v-for="option in filter.options"
                    :key="String(optionValue(option))"
                    :value="String(optionValue(option))"
                >
                    {{ optionLabel(option) }}
                </option>
            </select>

            <div class="tickets-index-filters__status">
                <button
                    type="button"
                    class="tickets-index-filters__status-btn"
                    aria-haspopup="true"
                    :aria-expanded="statusMenuOpen ? 'true' : 'false'"
                    @click.stop="toggleStatusMenu"
                >
                    Stato
                    <span class="tickets-index-filters__status-count">{{ statusCount }}</span>
                    <span aria-hidden="true">▾</span>
                </button>
                <div
                    v-if="statusMenuOpen"
                    class="tickets-index-filters__status-menu"
                    role="menu"
                    @click.stop
                >
                    <div class="tickets-index-filters__status-label">Stati visibili</div>
                    <div class="tickets-index-filters__status-sep" />
                    <button
                        v-for="status in statusOptions"
                        :key="optionValue(status)"
                        type="button"
                        class="tickets-index-filters__status-item"
                        role="menuitemcheckbox"
                        :aria-checked="isStatusChecked(optionValue(status)) ? 'true' : 'false'"
                        @click="toggleStatus(optionValue(status))"
                    >
                        <input
                            type="checkbox"
                            tabindex="-1"
                            :checked="isStatusChecked(optionValue(status))"
                            @click.prevent
                        />
                        {{ optionLabel(status) }}
                    </button>
                </div>
            </div>

            <div class="tickets-index-filters__actions">
                <button
                    v-if="showClassicToggle"
                    type="button"
                    class="tickets-index-btn"
                    :disabled="classicDisabled"
                    aria-label="Vista in tabella"
                    title="Utile per cercare un ticket specifico"
                    @click="$emit('classic-view')"
                >
                    Vista in tabella
                </button>
                <button
                    v-if="showSimulate"
                    type="button"
                    class="tickets-index-btn tickets-index-btn--primary"
                    @click="$emit('simulate')"
                >
                    <span aria-hidden="true">↻</span>
                    Simula cambio stato
                </button>
                <slot name="actions" />
            </div>
        </div>
        <div class="tickets-index-filters__hint">
            <span aria-hidden="true">☐</span>
            Scorri lateralmente per vedere tutte le colonne
        </div>
    </div>
</template>
